/* globals FB */
/**
 * @ngdoc service
 * @name eventix.common.services.FB
 * @description
 * Proxy service for Facebook SDK. Uses promises instead of callbacks
 */
export default angular.module('eventix.common.services.facebook', [])
    .provider('FB', function() {
        var fbAppId;
        this.setAppId = (appId) => {
            fbAppId = appId;
        };
        this.$get = function($window, $timeout, $q, $rootScope, UIMessages, LibraryLoader, $uibModal) {
            let loader = $q.defer();
            let loaded = loader.promise;
            let authorized = false;
            let user = {};

            if(!fbAppId)
                loader.reject('DISABLED');
            else {
                $window.fbAsyncInit = function() {
                    FB.init({
                        appId: fbAppId,
                        cookie: true,
                        status: true,
                        xfbml: true,
                        version: 'v2.7'
                    });
                    let timeout = $timeout(() => loader.reject('NOT_LOADED'), 10000);
                    FB.getLoginStatus(function(response) {
                        if (response.status === 'connected') {
                            authorized = true;
                            user = response.authResponse;
                        }
                        loader.resolve();
                        $timeout.cancel(timeout);
                        $rootScope.$broadcast('fb-loaded');
                    }, true);
                };
                LibraryLoader.load('//connect.facebook.net/en_US/sdk.js', { id: 'facebook-jssdk' });
            }

            return {
                disabled: !fbAppId || !fbAppId.length,
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#api
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Send a request to `FB.api`
                 *
                 * @param {Any} args Call this function like you would the `FB.api`, except without a callback
                 * @returns {Promise} Resolves or rejects with the API response
                 */
                api: function(...args) {
                    return loaded.then(() => {
                        var deferred = $q.defer();
                        FB.api(...args, function(response) {
                            if (!response)
                                return deferred.reject('EMPTY_RESPONSE');
                            if (response.error)
                                return deferred.reject(response.error);
                            return deferred.resolve(response);
                        });
                        return deferred.promise;
                    }).catch(err => {
                        if(err === 'NOT_LOADED')
                            UIMessages.push('Error loading Facebook');
                        else if(err === 'DISABLED')
                            UIMessages.push('Facebook integrations disabled');
                        throw err;
                    });
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#login
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Attempt to login a user through Facebook. If a user is
                 * already logged in, Facebook won't be queried, otherwise
                 * a popup will open, potentially prompting the user to
                 * authorise the app.
                 *
                 * Because of the aforementioned pop-up, only call this
                 * function as a result of a user performing an action, such
                 * as button click. Otherwise the pop-up is likely to be
                 * blocked.
                 *
                 * @param {String} scope Comma seperated list of scopes to request access to
                 * @returns {Promise} Resolves or rejects with the API response
                 */
                login: function(scope = 'public_profile,email,user_friends,rsvp_event') {
                    return loaded.then(() => {
                        return this.hasPermissions(scope).catch(error => {
                            if(typeof error === 'string' && error !== 'DISABLED')
                                return this._login(scope);
                            throw error;
                        });
                    }).catch(err => {
                        if(err === 'NOT_LOADED')
                            UIMessages.push('Error loading Facebook');
                        else if(err === 'DISABLED')
                            UIMessages.push('Facebook integrations disabled');
                        throw err;
                    });
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#hasPermissions
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Check if logged in user has given permission for `scope`
                 *
                 * @param {String} scope Comma separated list of Facebook scopes
                 * @return {Promise} Resolves with void or rejects with string of missing scopes
                 */
                hasPermissions: function(scope) {
                    return loaded.then(() => {
                        if (!authorized)
                            return $q.reject('NOT_LOGGED_IN');
                        return this.api(`/${user.userID}/permissions`);
                    }).then(response => {
                        let scopes = scope.split(',');
                        let rejected = _.filter(scopes, scope => {
                            return !_.find(response.data, { permission: scope, status: 'granted' });
                        });
                        if (rejected.length)
                            return $q.reject(rejected.join(','));
                        return $q.resolve();
                    }).catch(err => {
                        if(err === 'NOT_LOADED')
                            UIMessages.push('Error loading Facebook');
                        else if(err === 'DISABLED')
                            UIMessages.push('Facebook integrations disabled');
                        throw err;
                    });
                },
                _login: function(scope) {
                    return loaded.then(() => {
                        var deferred = $q.defer();
                        FB.login(function(response) {
                            if (!response)
                                return deferred.reject('EMPTY_RESPONSE');
                            if (response.error)
                                return deferred.reject(response.error);
                            if (!response.authResponse)
                                return deferred.reject('USER_DENIED');

                            authorized = true;
                            user = response.authResponse;
                            return deferred.resolve(response);
                        }, { scope: scope });
                        return deferred.promise;
                    });
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#currentUser
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Get the user credentials for the current user
                 *
                 * @returns {Object} User credentials object
                 */
                currentUser: function() {
                    return user;
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#isLoggedIn
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Checks if FB is authorized
                 *
                 * @returns {Boolean} True if FB is authorized
                 */
                isLoggedIn: function() {
                    return authorized;
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#isLoaded
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Check if SDK is correctly loaded
                 *
                 * @returns {Boolean} True if FB SDK is loaded
                 */
                isLoaded: function() {
                    return loaded.$$state.status === 1;
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#getMyPages
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Get pages that I'm admin of
                 *
                 * @returns {Promise<Array>} Resolves with pages `{ id, name, access_token }`
                 */
                getMyPages: function() {
                    return this.login('manage_pages')
                        .then(() => this.api('/me/accounts?fields=id,name,access_token,link,fan_count'))
                        .then(response => {
                            return response.data;
                        });
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#getMyEvents
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Get events I'm an admin of
                 *
                 * The returned array is augmented with `after` and `before` functions, because results are paginated
                 *
                 * @return {Array<Object>} Array of events
                 */
                getMyEvents: function() {
                    let url = 'me/events?fields=name,is_viewer_admin,start_time,end_time,place{name,location{latitude,longitude,street,city}}';
                    let that = this;
                    return this.login('user_events')
                        .then(() => this.api(url))
                        .then(handleResponse);

                    function handleResponse(response) {
                        let events = response.data; //_.filter(response.data, { is_viewer_admin: true });
                        Object.defineProperties(events, {
                            after: {
                                value: function() {
                                    return that.api(url + '&limit=25&after=' + response.paging.after)
                                        .then(handleResponse);
                                }
                            },
                            before: {
                                value: function() {
                                    return that.api(url + '&limit=25&before=' + response.paging.before)
                                        .then(handleResponse);
                                }
                            }
                        });
                        return events;
                    }
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#checkAppStatusOnPage
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Check if our app is installed on a page
                 *
                 * @param {Object} page Page with `{ id, access_token }`
                 * @return {Promise<String>} verified, missing or unknown
                 */
                checkAppStatusOnPage: function(page) {
                    return this.api(`/${page.id}/tabs?fields=application`, 'GET', {
                        access_token: page.access_token
                    }).then(response => {
                        let filter = { application: { id: FB_APP_ID_SHOP }};
                        let installed = !_.isNil(_.find(response.data, filter));
                        return installed ? 'verified' : 'missing';
                    }).catch(() => {
                        return 'unknown';
                    });
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#installApp
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Install our app is installed on the page
                 *
                 * @param {Object} page Page with `{ id, access_token }`
                 * @return {Promise<Response>} Facebook API response
                 */
                installApp: function(page) {
                    return this.api(`/${page.id}/tabs`, 'POST', {
                        access_token: page.access_token,
                        app_id: FB_APP_ID_SHOP,
                        custom_name: 'Ticketshop'
                    });
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#removeApp
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Remove our app from a page
                 *
                 * @param {Object} page Page with `{ id, access_token }`
                 * @return {Promise<Response>} Facebook API response
                 */
                removeApp: function(page) {
                    return this.api(`/${page.id}/tabs`, 'DELETE', {
                        access_token: page.access_token,
                        tab: 'app_' + FB_APP_ID_SHOP
                    });
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.FB#autoMaybe
                 * @methodOf eventix.common.services.FB
                 * @description
                 * Set a facebook user to "interested" for a set of Facebook events
                 * @param {Array<String>} eventIds List of eventIds
                 * @returns {Promise} Resolves when done
                 */
                autoMaybe(eventIds) {
                    if(!eventIds || !eventIds.length)
                        return $q.resolve();
                    return this.hasPermissions('rsvp_event').then(() => {
                        let promises = _.map(eventIds, id => {
                            return this.api(`/${id}/maybe`, 'post');
                        });
                        return $q.all(promises);
                    });
                },
                /**
                 * Set the user "Attending" for a list of Facebook events - Won't ask for permission
                 * @param {Array<String>} eventIds Facebook EventIDs
                 * @returns {Promise} Resolves when done
                 */
                autoAttend(eventIds) {
                    if(!eventIds || !eventIds.length)
                        return $q.resolve();
                    return this.hasPermissions('rsvp_event').then(() => {
                        let promises = _.map(eventIds, id => {
                            return this.api(`/${id}/attending`, 'post' );
                        });
                        return $q.all(promises);
                    });
                },
                /**
                 * Set the user "Attending" for a list of Facebook events - Ask for permissions if we don't have them yet
                 * @param {Array<String>} eventIds Facebook EventIDs
                 * @returns {Promise} Resolves when done
                 */
                attend(eventIds) {
                    return this.login('public_profile,email,rsvp_event').then(() => {
                        let promises = _.map(eventIds, id => {
                            return this.api(`/${id}/attending`, 'post' );
                        });
                        return $q.all(promises);
                    });
                },
                /**
                 * Set the user "Interested" for a list of Facebook events.
                 * Ask for confirmation. Can't delete a users attendance completely, so interest is the next best thing
                 * @param {Array<String>} eventIds Facebook EventIDs
                 * @returns {Promise} Resolves when done
                 */
                unattend(eventIds) {
                    let modal = $uibModal.open({
                        template: `<div class="modal-content">
                                        <div class="modal-header">
                                            <h4 class="modal-title"><translate translate-values="{count: 0}">common.shop.facebookAttend</translate>?</h4>
                                        </div>
                                        <div class="modal-body form-horizontal"> <p translate="common.shop.facebookUnattendConfirm"></p></div>
                                        <div class="modal-footer">
                                            <button class="btn btn-default" ng-click="$dismiss()" translate="common.action.no"></button>
                                            <button class="btn btn-danger" ng-click="$close()" translate="common.action.yes"></button>
                                        </div>
                                    </div>`,
                        size: 'sm'
                    });
                    return modal.result
                        .then(() => FB.login('public_profile,email,rsvp_event'))
                        .then(() => {
                            let promises = _.map(eventIds, id => {
                                return this.api(`/${id}/maybe`, 'post');
                            });
                            return $q.all(promises);
                        });
                }
            };
        };
    })
    .component('facebookPageBanner', {
        bindings: {
            url: '@'
        },
        controller: function($element) {
            const $ctrl = this;
            $ctrl.$postLink = function() {
                $element.css({
                    width: '100%',
                    overflow: 'hidden',
                    display: 'block',
                    textAlign: 'center',
                    id: 'social-container'
                });
                var facebookUrl = encodeURIComponent($ctrl.url),
                    iFrameSrc = [
                        'https://www.facebook.com/plugins/page.php?href\x3d',
                        facebookUrl,
                        '%2F\x26tabs\x26width\x3d500\x26height\x3d70\x26small_header\x3dtrue\x26adapt_container_width',
                        '\x3dtrue\x26hide_cover\x3dfalse\x26show_facepile\x3dfalse\x26appId\x3d993653613992268'
                    ].join(''),
                    iFrame = angular.element(`<iframe src="${iFrameSrc}"></iframe>`);
                iFrame.css({
                    border: 'none',
                    width: '100%',
                    maxWidth: '500px',
                    height: '70px',
                    margin: '0 auto'
                });
                $element.append(iFrame);
            };
        }
    }).name;
